import { useQuery } from '@tanstack/react-query'
import { Badge } from '@/components/ui/badge'
import { api } from '@/lib/api'

/**
 * Threat actor "Recent Claims" tab body (Plan B Plan 2 T15, spec §5.3).
 *
 * Wraps `GET /api/leak-sites/by-threat-actor/:actorId?days=30` (T6) and
 * renders one section per linked ransomware group, with a list of
 * anonymised claim rows under each.
 *
 * The visibility of the Recent Claims tab itself is gated on
 * `useActorHasLeakSiteActivity` (below) at the page level — when no
 * `ransomware_groups` row links to this actor, the tab is hidden entirely.
 *
 * Per spec §5.6 / §5.7 the per-claim row only carries sector / country /
 * size band / claim date / source / related CVEs — the layer-2 projector
 * has already stripped victim identity.
 *
 * NOTE: Surface 1 ships a shared `LeakSiteClaimCard.tsx` primitive (renamed
 * from `AnonymisedClaimCard.tsx` in P12, 2026-05-02). When the actor-claims
 * endpoint adds groupName + threatActorId enrichment, the inline `<ClaimRow>`
 * here can be replaced with that import; until then it stays inline because
 * the actor-claims response doesn't carry the enrichment fields.
 */

export interface AnonymisedClaim {
  id: string
  groupId: string
  source: string
  externalId: string
  /** ISO-8601 string from the API; the `Layer2Claim` projector emits Date but
   *  it round-trips through JSON so the wire format is a string. */
  claimDate: string
  victimCountry: string | null
  victimSector: string | null
  victimSizeBand: string | null
  relatedCves: string[] | null
  retrievedAt: string
}

interface ActorClaimsResponse {
  groups: Array<{
    groupName: string
    claims: AnonymisedClaim[]
  }>
}

const DAYS = 30

/** Convenience hook for the page: returns `true` when the API reports any
 *  linked ransomware group. Used to gate tab visibility per §5.3.
 */
export function useActorHasLeakSiteActivity(actorId: string | undefined) {
  const { data } = useQuery({
    queryKey: ['leak-sites', 'by-threat-actor', actorId ?? '', DAYS],
    queryFn: () =>
      api.get<ActorClaimsResponse>(
        `/leak-sites/by-threat-actor/${actorId}?days=${DAYS}`,
      ),
    enabled: Boolean(actorId),
    staleTime: 60_000,
    retry: false,
  })
  return (data?.groups.length ?? 0) > 0
}

export interface RecentClaimsTabProps {
  actorId: string
}

export function RecentClaimsTab({ actorId }: RecentClaimsTabProps) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['leak-sites', 'by-threat-actor', actorId, DAYS],
    queryFn: () =>
      api.get<ActorClaimsResponse>(
        `/leak-sites/by-threat-actor/${actorId}?days=${DAYS}`,
      ),
    enabled: Boolean(actorId),
    staleTime: 60_000,
    retry: false,
  })

  if (isLoading) {
    return (
      <div className="p-4 text-sm text-muted-foreground">
        Loading recent claims…
      </div>
    )
  }
  if (isError || !data || data.groups.length === 0) {
    return (
      <div className="p-4 text-sm text-muted-foreground">
        No recent ransomware leak-site claims linked to this actor.
      </div>
    )
  }

  return (
    <div className="space-y-6 p-2">
      {data.groups.map((g) => (
        <section key={g.groupName} aria-labelledby={`group-${slug(g.groupName)}`}>
          <h3
            id={`group-${slug(g.groupName)}`}
            className="mb-2 text-base font-semibold"
          >
            {g.groupName}
          </h3>
          {g.claims.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No claims in the last 30 days.
            </p>
          ) : (
            <ul className="space-y-2">
              {g.claims.map((c) => (
                <ClaimRow key={c.id} claim={c} />
              ))}
            </ul>
          )}
        </section>
      ))}
    </div>
  )
}

function slug(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '-')
}

function ClaimRow({ claim }: { claim: AnonymisedClaim }) {
  const date = formatDate(claim.claimDate)
  return (
    <li className="rounded-md border border-border bg-card p-3">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-sm">
        {claim.victimSector && (
          <span className="font-medium">{claim.victimSector}</span>
        )}
        {claim.victimCountry && (
          <span className="font-mono text-xs text-muted-foreground">
            {claim.victimCountry}
          </span>
        )}
        {claim.victimSizeBand && (
          <Badge variant="secondary" className="text-xs">
            {claim.victimSizeBand}
          </Badge>
        )}
        {date && (
          <span className="ml-auto text-xs text-muted-foreground tabular-nums">
            {date}
          </span>
        )}
      </div>
      {claim.relatedCves && claim.relatedCves.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {claim.relatedCves.map((cve) => (
            <Badge key={cve} variant="outline" className="font-mono text-xs">
              {cve}
            </Badge>
          ))}
        </div>
      )}
      <p className="mt-1 text-xs text-muted-foreground">Source: {claim.source}</p>
    </li>
  )
}

function formatDate(value: string): string | null {
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return null
  return d.toLocaleDateString()
}
